import { useState } from "react";
import { assetUrl, useBrand } from "@/lib/brand";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { codigoValido, inicioCaducado } from "@/lib/segundoFactor";
import { toast } from "sonner";

export default function AdminLogin() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { login, loginTotp } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Segundo paso (segundo factor): el token que da la contraseña y el código de la app o de recuperación.
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (mfa && !codigoValido(code)) {
      toast.error("Escribe los 6 números de la app o un código de recuperación (XXXX-XXXX)");
      return;
    }
    setLoading(true);
    try {
      if (!mfa) {
        const res = await login(email, password);
        if (res?.mfa_required) {
          setMfa(res.mfa_token);
          setCode("");
          return;
        }
      } else {
        await loginTotp(mfa, code);
      }
      toast.success("Bienvenido");
      navigate("/admin");
    } catch (err) {
      toast.error(formatError(err));
      if (inicioCaducado(err)) {
        setMfa(null);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        {/* Sin fotos de bancos de imágenes externos: cada visita les daría la IP de quien entra. */}
        <div className="absolute inset-0 bg-gradient-to-br from-marca-hover via-marca to-marca" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="text-overline text-white/80 hover:underline" data-testid="admin-back-home">
            ← {brand?.brand_name || "Inicio"}
          </Link>
          <div>
            <span className="text-overline text-white/80">Panel propietario</span>
            <h1 className="font-display text-5xl font-black tracking-tighter mt-3 leading-[0.95]">
              Gestiona tus
              <br />
              alquileres.
            </h1>
            <p className="text-white/80 mt-6 max-w-md leading-relaxed">
              Crea reservas, comparte códigos con tus clientes y revisa
              check-ins, fotos, firmas e incidencias en un solo lugar.
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm text-white/80">
            {brand?.logo_url && <img src={assetUrl(brand.logo_url)} alt="" className="h-10 w-auto bg-white/90 rounded p-1" />}
            <span>{brand?.brand_name || ""}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F7F5F0]">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="lg:hidden inline-flex items-center text-sm text-[#5C5C5C] mb-8"
            data-testid="admin-mobile-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Link>
          <div className="h-12 w-12 bg-marca rounded-xl flex items-center justify-center text-white mb-6">
            <Lock className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] mb-2">
            Acceso propietario
          </h2>
          <p className="text-[#5C5C5C] mb-8">
            {mfa
              ? "Escribe el código de 6 cifras de tu app de autenticación o uno de tus códigos de recuperación."
              : "Introduce tus credenciales para gestionar la flota."}
          </p>
          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            {mfa ? (
              <div>
                <Label htmlFor="totp" className="text-[#1A1A1A]">Código de la app de autenticación o código de recuperación</Label>
                <Input
                  id="totp"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  maxLength={20}
                  className="mt-2 bg-white border-[#DCD8D0] h-12 tracking-widest"
                  data-testid="login-totp"
                />
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="email" className="text-[#1A1A1A]">Correo</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2 bg-white border-[#DCD8D0] h-12"
                    data-testid="admin-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[#1A1A1A]">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-2 bg-white border-[#DCD8D0] h-12"
                    data-testid="admin-password-input"
                  />
                </div>
              </>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-marca hover:bg-marca-hover text-white h-12 text-base"
              data-testid="admin-login-submit"
            >
              {loading ? "Entrando..." : mfa ? "Comprobar el código" : "Entrar al panel"}
            </Button>
            {mfa ? (
              <button
                type="button"
                onClick={() => { setMfa(null); setCode(""); }}
                className="block w-full text-center text-sm text-[#5C5C5C] underline"
                data-testid="login-totp-back"
              >
                Volver
              </button>
            ) : (
              <Link to="/admin/recuperar" className="block text-center text-sm text-[#5C5C5C] underline" data-testid="admin-forgot-link">
                He olvidado la contraseña
              </Link>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
